import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  TablePagination,
} from "@mui/material";
import {
  DirectionsCar,
  People,
  Assignment,
  CalendarToday,
  CheckCircle,
  Pending,
  Cancel,
  MonetizationOn,
} from '@mui/icons-material';
import { Refresh as RefreshIcon, Search as SearchIcon } from "@mui/icons-material";
import { getServicePerformanceReport } from "../services/reportService";

const ServicePerformanceReport: React.FC = () => {
  const [report, setReport] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const fetchReport = async () => {
    try {
      setLoading(true);
      const data = await getServicePerformanceReport({ page, limit, fromDate, toDate });
      setReport(data.data);
      setError(null);
    } catch (err) {
      console.error("Error fetching Service Performance Report:", err);
      setError("Failed to load report. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
  }, [page, limit]);

  if (loading && !report) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <div >
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        
        <Typography variant="h4" gutterBottom>
          Service Performance Report
        </Typography>
      </div>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {/* Average Service Time */}
      <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
<Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", marginBottom: 3 }}>
          <TextField
            label="From Date"
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
          />
          <TextField
            label="To Date"
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
          />

          <Button
            variant="contained"
            color="primary"
            startIcon={<SearchIcon />}
            onClick={() => {
              setPage(1);
              fetchReport();
            }}
          >
            Search
          </Button>

          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => {
              setFromDate("");
              setToDate("");
              setPage(1);
              fetchReport();
            }}
          >
            Reset
          </Button>
        </Box>

          {/* Summary */}
      {report?.summary && (
  <Box sx={{ mb: 4 }}>
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "repeat(2, minmax(220px, 1fr))",
        gap: 3,
      }}
    >
      {[
        {
          title: "Most Popular",
          value: report.summary.most_popular || "N/A",
          color: "#1976d2",
          icon: <DirectionsCar sx={{ fontSize: 38, color: '#1976d2' }} />,
        },
        {
          title: "Least Popular",
          value: report.summary.least_popular || "N/A",
          icon: "❄️",
          color: "#d32f2f",
        },
        {
          title: "Completed",
          value: report.summary.total_completed,
          icon: "✅",
          color: "#2e7d32",
        },
        {
          title: "Cancelled",
          value: report.summary.total_cancelled,
          icon: "🚫",
          color: "#f57c00",
        },
      ].map((card, index) => (
        <Paper
          key={index}
          elevation={3}
          sx={{
            p: 3,
            borderRadius: 2,
            boxShadow: 0,
            border: 1,
            borderColor: '#e0e0e0',
            transition: "transform 0.3s ease",
            "&:hover": { transform: "translateY(-5px)" },
          }}
        >
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box>
              <Typography variant="body2" color="text.secondary">
                {card.title}
              </Typography>
              <Typography variant="h6" fontWeight="bold">
                {card.value}
              </Typography>
            </Box>
            <Box
              sx={{
                fontSize: 36,
                color: card.color,
              }}
            >
              {card.icon}
            </Box>
          </Box>
        </Paper>
      ))}
    </Box>
  </Box>
)}

        <Typography variant="h6" sx={{ mb: 2 }}>
          Average Service Time (in minutes)
        </Typography>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Service Name</TableCell>
                <TableCell>Average Duration (Minutes)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {report?.average_service_times?.rows?.length ? (
                report.average_service_times.rows.map((row: any, index: number) => (
                  <TableRow key={index}>
                    <TableCell>{row.service_name}</TableCell>
                    <TableCell>{row.avg_duration_minutes}</TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={2} align="center">
                    No data available
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/*<TablePagination
          component="div"
          count={report?.average_service_times?.total_count || 0}
          page={page - 1}
          onPageChange={(event, newPage) => setPage(newPage + 1)}
          rowsPerPage={limit}
          onRowsPerPageChange={(event) => {
            setLimit(parseInt(event.target.value, 10));
            setPage(1);
          }}
          rowsPerPageOptions={[5, 10, 25, 50]}
          labelRowsPerPage="Rows per page:"
        />*/}
      </Paper>
    </div>
  );
};

export default ServicePerformanceReport;
